// src/features/developers/components/KeysPage.tsx
//
// Staff-only: the access keys of the public data API. Outside the four tabs of the documentation
// (reached from a button of the page, and only offered when the API says the person may manage
// keys). Creating shows the secret ONCE; revoking asks first. The API enforces who may do any of it.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocale, useNotify, useTranslate } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBackRounded as BackIcon,
  CheckRounded as CopiedIcon,
  ContentCopyRounded as CopyIcon,
  VpnKeyOutlined as KeyIcon,
  WarningAmberRounded as WarningIcon,
} from '@mui/icons-material';
import { SectionHeader } from '@/components/common/SectionHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { useV2Properties } from '@/hooks/useV2PropertySelection';
import type { V2Property } from '@/types/analyticsV2';
import { developersApi, type ApiFailure } from '../api';
import { DEVELOPERS_ROUTE } from '../access';
import { failureTextKey, prependKey, propertyNames, replaceKey, toCreateRequest, validateKeyForm, type KeyForm, type KeyFormField } from '../keys';
import type { AccessKey, CreatedAccessKey } from '../types';
import { MONO, useCopy } from './common';

const allProperties = (properties: readonly V2Property[]): V2Property[] => [...properties];

const useFormatDate = () => {
  const locale = useLocale();
  return useCallback(
    (iso: string | null): string => (iso ? new Date(iso).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : ''),
    [locale]
  );
};

const EMPTY_FORM: KeyForm = { name: '', ownerName: '', propertyIds: [] };

export const KeysPage: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const formatDate = useFormatDate();
  const t = (key: string, options?: Record<string, unknown>): string => translate(`developers.keys.${key}`, options);

  const [keys, setKeys] = useState<AccessKey[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedAccessKey | null>(null);
  const [revoking, setRevoking] = useState<AccessKey | null>(null);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    developersApi
      .listKeys()
      .then((list) => alive && setKeys(Array.isArray(list) ? list : []))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [attempt]);

  const statusChip = (key: AccessKey) => (
    <Chip
      size="small"
      label={t(`status.${key.status}`)}
      color={key.status === 'active' ? 'success' : 'default'}
      variant={key.status === 'active' ? 'filled' : 'outlined'}
    />
  );

  const propertySummary = (key: AccessKey): string => {
    const names = propertyNames(key);
    const count = t('propertiesCount', { smart_count: key.properties.length });
    return names.length > 0 && names.length <= 2 ? names.join(', ') : count;
  };

  const renderList = () => {
    if (failed) return <FetchErrorBanner onRetry={() => setAttempt((n) => n + 1)} />;
    if (!keys) return <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />;
    if (keys.length === 0) return <EmptyState icon={<KeyIcon />} message={t('empty')} />;

    if (isMobile) {
      return (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
          {keys.map((key) => (
            <Card component="li" key={key.id} sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{key.name}</Typography>
                {statusChip(key)}
              </Box>
              <Box component="dl" sx={{ m: 0, mt: 1, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 1.5, rowGap: 0.5, fontSize: '0.875rem' }}>
                {[
                  [t('columns.owner'), key.ownerName],
                  [t('columns.properties'), propertySummary(key)],
                  [t('columns.key'), key.prefix],
                  [t('columns.createdBy'), `${key.createdBy} · ${formatDate(key.createdAtUtc)}`],
                  [t('columns.lastUsed'), key.lastUsedAtUtc ? formatDate(key.lastUsedAtUtc) : t('neverUsed')],
                ].map(([label, value]) => (
                  <React.Fragment key={label}>
                    <Box component="dt" sx={{ color: theme.palette.text.secondary }}>{label}</Box>
                    <Box component="dd" sx={{ m: 0, overflowWrap: 'anywhere', fontFamily: label === t('columns.key') ? MONO : undefined }}>{value}</Box>
                  </React.Fragment>
                ))}
              </Box>
              {key.status === 'active' && (
                <Button size="small" color="error" variant="outlined" onClick={() => setRevoking(key)} sx={{ mt: 1.5, minHeight: 44, color: theme.palette.error.dark }}>
                  {t('revoke')}
                </Button>
              )}
            </Card>
          ))}
        </Box>
      );
    }

    return (
      <Card sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['name', 'owner', 'properties', 'key', 'createdBy', 'created', 'lastUsed', 'status'].map((column) => (
                <TableCell key={column} sx={{ whiteSpace: 'nowrap' }}>{t(`columns.${column}`)}</TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((key) => (
              <TableRow key={key.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{key.name}</TableCell>
                <TableCell>{key.ownerName}</TableCell>
                <TableCell>{propertySummary(key)}</TableCell>
                <TableCell sx={{ fontFamily: MONO, whiteSpace: 'nowrap' }}>{key.prefix}</TableCell>
                <TableCell>{key.createdBy}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(key.createdAtUtc)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{key.lastUsedAtUtc ? formatDate(key.lastUsedAtUtc) : t('neverUsed')}</TableCell>
                <TableCell>{statusChip(key)}</TableCell>
                <TableCell align="right">
                  {key.status === 'active' && (
                    <Button size="small" color="error" onClick={() => setRevoking(key)} sx={{ color: theme.palette.error.dark }}>
                      {t('revoke')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  return (
    <Box sx={{ pb: 6, minWidth: 0 }}>
      <Box sx={{ mb: 1 }}>
        <Button component={RouterLink} to={DEVELOPERS_ROUTE} size="small" startIcon={<BackIcon />} sx={{ color: theme.palette.text.secondary }}>
          {t('back')}
        </Button>
      </Box>
      <SectionHeader title={t('title')} subtitle={t('subtitle')} sticky={false}
        rightActions={
          <Button variant="contained" size="small" onClick={() => setCreating(true)}>
            {t('create')}
          </Button>
        }
      />
      <Box sx={{ mt: 2 }}>{renderList()}</Box>

      {creating && (
        <CreateKeyDialog
          onClose={() => setCreating(false)}
          onCreated={(key) => {
            setCreating(false);
            setCreated(key);
            setKeys((current) => prependKey(current ?? [], key));
          }}
        />
      )}
      {created && <CreatedKeyDialog created={created} onClose={() => setCreated(null)} />}
      {revoking && (
        <Dialog open onClose={() => setRevoking(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('revokeDialog.title', { name: revoking.name })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('revokeDialog.text')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRevoking(null)}>{t('revokeDialog.cancel')}</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                const target = revoking;
                developersApi
                  .revokeKey(target.id)
                  .then((updated) => setKeys((current) => replaceKey(current ?? [], updated)))
                  .catch(() => notify(`developers.keys.revokeDialog.failed`, { type: 'error' }))
                  .finally(() => setRevoking(null));
              }}
            >
              {t('revokeDialog.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

const CreateKeyDialog: React.FC<{ onClose: () => void; onCreated: (key: CreatedAccessKey) => void }> = ({ onClose, onCreated }) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>): string => translate(`developers.keys.${key}`, options);
  const { data: properties = [] } = useV2Properties(allProperties);
  const sorted = useMemo(() => [...properties].sort((a, b) => a.displayName.localeCompare(b.displayName)), [properties]);
  const [form, setForm] = useState<KeyForm>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const errors = validateKeyForm(form);
  const fieldError = (field: KeyFormField): string | undefined => (submitted && errors[field] ? t(`form.invalid.${errors[field]}`) : undefined);
  const selected = sorted.filter((property) => form.propertyIds.includes(property.id));

  const submit = (): void => {
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    setFailure(null);
    developersApi
      .createKey(toCreateRequest(form))
      .then(onCreated)
      .catch((error: ApiFailure) => {
        setFailure(t(`failures.${failureTextKey(error.code)}`));
        setSaving(false);
      });
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('form.title')}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        <TextField
          label={t('form.name')}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={Boolean(fieldError('name'))}
          helperText={fieldError('name') ?? t('form.nameHelp')}
          autoFocus
          fullWidth
        />
        <TextField
          label={t('form.owner')}
          value={form.ownerName}
          onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
          error={Boolean(fieldError('owner'))}
          helperText={fieldError('owner') ?? t('form.ownerHelp')}
          fullWidth
        />
        <Box>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={sorted}
            value={selected}
            noOptionsText={t('form.noProperties')}
            getOptionLabel={(option) => option.displayName}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setForm({ ...form, propertyIds: value.map((property) => property.id) })}
            renderOption={(props, option, { selected: isSelected }) => (
              <li {...props} key={option.id}>
                <Checkbox size="small" checked={isSelected} sx={{ mr: 1 }} />
                {option.displayName}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label={t('form.properties')} error={Boolean(fieldError('properties'))} helperText={fieldError('properties') ?? t('form.propertiesHelp')} />
            )}
          />
          <Button size="small" sx={{ mt: 0.5 }} disabled={sorted.length === 0} onClick={() => setForm({ ...form, propertyIds: sorted.map((property) => property.id) })}>
            {t('form.selectAll')}
          </Button>
        </Box>
        {failure && (
          <Typography role="alert" color="error.dark" sx={{ fontSize: '0.875rem' }}>
            {failure}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('form.cancel')}
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {t('form.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** The only moment the secret is on screen: it cannot be dismissed by a click outside. */
const CreatedKeyDialog: React.FC<{ created: CreatedAccessKey; onClose: () => void }> = ({ created, onClose }) => {
  const translate = useTranslate();
  const theme = useTheme();
  const t = (key: string): string => translate(`developers.keys.created.${key}`);
  const { copiedId, copy } = useCopy();
  const copied = copiedId === 'created-key';
  return (
    <Dialog open maxWidth="sm" fullWidth onClose={(_, reason) => reason === 'escapeKeyDown' && onClose()}>
      <DialogTitle>{t('title')}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: theme.palette.warning.dark }}>
          <WarningIcon />
          <Typography sx={{ fontWeight: 600 }}>{t('warning')}</Typography>
        </Box>
        <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>{t('text')}</Typography>
        <Box
          sx={{
            fontFamily: MONO,
            fontSize: '0.875rem',
            p: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.custom.radius.card,
            backgroundColor: theme.palette.background.default,
            overflowWrap: 'anywhere',
            userSelect: 'all',
          }}
        >
          {created.apiKey}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" startIcon={copied ? <CopiedIcon /> : <CopyIcon />} onClick={() => copy('created-key', created.apiKey)}>
          {t(copied ? 'copied' : 'copy')}
        </Button>
        <Button variant="contained" onClick={onClose}>
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
